/**
 * Time model: 1 real day = 1 game year.
 * The game calendar uses 12 months of 30 days (360-day year) so that
 * one game day is exactly 4 real minutes:
 *   1 game day   = 240 000 ms  (4 min)
 *   1 game month = 30 days     (2 h)
 *   1 season     = 90 days     (6 h)
 *   1 game year  = 360 days    (24 h)
 */
export const MS_PER_GAME_DAY = 240_000;
export const DAYS_PER_MONTH = 30;
export const MONTHS_PER_YEAR = 12;
export const DAYS_PER_YEAR = DAYS_PER_MONTH * MONTHS_PER_YEAR; // 360
export const DAYS_PER_SEASON = 90;

/** Game day 0 is 1 Jan of this year. */
export const CALENDAR_START_YEAR = 1000;

export type Season = 'spring' | 'summer' | 'autumn' | 'winter';

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

export interface GameDate {
  year: number;
  /** 1..12 */
  month: number;
  /** 1..30 */
  day: number;
  season: Season;
}

export function dayToDate(day: number): GameDate {
  const d = Math.floor(day);
  const year = CALENDAR_START_YEAR + Math.floor(d / DAYS_PER_YEAR);
  const dayOfYear = ((d % DAYS_PER_YEAR) + DAYS_PER_YEAR) % DAYS_PER_YEAR;
  const month = Math.floor(dayOfYear / DAYS_PER_MONTH) + 1;
  const dom = (dayOfYear % DAYS_PER_MONTH) + 1;
  return { year, month, day: dom, season: seasonOf(d) };
}

/** Spring = Mar–May, Summer = Jun–Aug, Autumn = Sep–Nov, Winter = Dec–Feb. */
export function seasonOf(day: number): Season {
  const dayOfYear = ((Math.floor(day) % DAYS_PER_YEAR) + DAYS_PER_YEAR) % DAYS_PER_YEAR;
  const month = Math.floor(dayOfYear / DAYS_PER_MONTH) + 1;
  if (month >= 3 && month <= 5) return 'spring';
  if (month >= 6 && month <= 8) return 'summer';
  if (month >= 9 && month <= 11) return 'autumn';
  return 'winter';
}

export function formatDate(day: number): string {
  const g = dayToDate(day);
  return `${g.day} ${MONTH_NAMES[g.month - 1]} ${g.year}`;
}

/** Whole game years between two game days (e.g. age). */
export function yearsBetween(fromDay: number, toDay: number): number {
  return Math.floor((toDay - fromDay) / DAYS_PER_YEAR);
}

export function isFirstDayOfMonth(day: number): boolean {
  return day % DAYS_PER_MONTH === 0;
}

export function isFirstDayOfYear(day: number): boolean {
  return day % DAYS_PER_YEAR === 0;
}

/** Game day for a real timestamp, given the real time at which game day 0 started. */
export function gameDayAt(nowMs: number, epochMs: number): number {
  return Math.floor((nowMs - epochMs) / MS_PER_GAME_DAY);
}

/** Real timestamp at which a game day starts. */
export function realMsOfDay(day: number, epochMs: number): number {
  return epochMs + day * MS_PER_GAME_DAY;
}

// ------------------------------------------------------------------ clock (in-game hours)

export const HOURS_PER_DAY = 24;
/** 1 real day = 24 in-game hours, so 1 in-game hour = 10 000 real ms. */
export const MS_PER_GAME_HOUR = MS_PER_GAME_DAY / HOURS_PER_DAY;

/** Absolute hour = day * 24 + hour (hour defaults to the start of the day). */
export function absHourOf(day: number, hour = 0): number {
  return Math.floor(day) * HOURS_PER_DAY + hour;
}

/** Day part of an absolute hour. */
export function dayOfAbsHour(absHour: number): number {
  return Math.floor(absHour / HOURS_PER_DAY);
}

/** Hour-within-day part of an absolute hour (0..23). */
export function hourOfAbsHour(absHour: number): number {
  return ((absHour % HOURS_PER_DAY) + HOURS_PER_DAY) % HOURS_PER_DAY;
}

/** e.g. "14 September 1010 · 06:00" — same wording as formatDate plus a zero-padded hour. */
export function formatDateTime(absHour: number): string {
  return `${formatDate(dayOfAbsHour(absHour))} · ${String(hourOfAbsHour(absHour)).padStart(2, '0')}:00`;
}

/** Real days -> game days (1 real day = 1 game year). */
export function realDaysToGameDays(realDays: number): number {
  return realDays * DAYS_PER_YEAR;
}
